import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Shield, FileText, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;
    const pollInterval = 2000;

    const poll = async () => {
      try {
        const data = await api.getCheckoutStatus(sessionId);
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setStatus('timeout');
        }
      } catch {
        setStatus('error');
      }
    };

    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center p-6" data-testid="checkout-success-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-[#DCE4ED] p-8 max-w-md w-full text-center"
      >
        {status === 'polling' && (
          <>
            <Loader2 size={48} className="text-[#00AEEF] animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#0D1B2A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Processing Payment...
            </h2>
            <p className="text-[#3D5A80]">Please wait while we confirm your payment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-[#1A3A64]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-[#1A3A64]" />
            </div>
            <h2 className="text-xl font-semibold text-[#0D1B2A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Payment Successful!
            </h2>
            <p className="text-[#3D5A80] mb-1">Your policy is now active.</p>
            {paymentData && (
              <p className="text-sm text-[#8DA0B9] mb-6">
                Amount: ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
              </p>
            )}
            <div className="flex flex-col gap-3">
              <Link to="/builder">
                <Button className="w-full btn-primary" data-testid="back-to-builder-btn">
                  <FileText size={16} className="mr-2" />
                  Back to Policy Builder
                </Button>
              </Link>
            </div>
          </>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <div className="w-16 h-16 bg-[#E74C3C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle size={32} className="text-[#E74C3C]" />
            </div>
            <h2 className="text-xl font-semibold text-[#0D1B2A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {status === 'expired' ? 'Session Expired' : 'Payment Issue'}
            </h2>
            <p className="text-[#3D5A80] mb-6">
              {status === 'expired' ? 'Your payment session has expired. Please try again.' : 'There was an issue confirming your payment. Please try again or contact support.'}
            </p>
            <Link to="/builder">
              <Button variant="outline" className="w-full" data-testid="retry-btn">
                <ArrowLeft size={16} className="mr-2" />
                Back to Builder
              </Button>
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default CheckoutSuccess;
